import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { TutorialProvider } from './context/TutorialContext';
import TutorialOverlay from './components/TutorialOverlay';
import AssistantChatWidget from './components/AssistantChatWidget';
import LoginPage from './pages/LoginPage';
import HomepageView from './components/views/home/HomepageView';
import AvailableUnitsView from './components/views/home/AvailableUnitsView';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import UserGuidePage from './pages/UserGuidePage';
import Dashboard from './pages/Dashboard';
import FRPModule from './pages/FRPModule-clean';
import GroupsModule from './pages/GroupsModule';
import UTBillsModule from './pages/UTBillsModule';
import AdminPage from './pages/AdminPage';
import AdminSMSPage from './pages/AdminSMSPage';
import AdminSMSHistoryPage from './pages/AdminSMSHistoryPage';
import AdminEmailPage from './pages/AdminEmailPage';
import AdminEmailHistoryPage from './pages/AdminEmailHistoryPage';
import AdminAddDepositPage from './pages/AdminAddDepositPage';
import AdminManageWalletPage from './pages/AdminManageWalletPage';
import AdminKraPage from './pages/AdminKraPage';
import AdminUsersPage from './pages/AdminUsersPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import AdminSupportTicketsPage from './pages/AdminSupportTicketsPage';
import AdminMpesaReconciliationPage from './pages/AdminMpesaReconciliationPage';
import AdminMpesaPullPage from './pages/AdminMpesaPullPage';
import AdminAgencyTrialsPage from './pages/AdminAgencyTrialsPage';
import PrivateRoute from './components/PrivateRoute';
import LockedPage from './pages/LockedPage';
import ImpersonationBanner from './components/ImpersonationBanner';
import AdminSessionGuard from './components/AdminSessionGuard';
import ScopedImpersonationGuard from './components/ScopedImpersonationGuard';
import PresenceHeartbeat from './components/PresenceHeartbeat';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('RecordLOGS render error:', error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const resetSession = () => {
      try { window.localStorage.clear(); } catch (_) {}
      try { window.sessionStorage.clear(); } catch (_) {}
      window.location.assign('/login');
    };

    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-5">
        <div className="max-w-sm w-full rounded-lg border border-emerald-100 bg-emerald-50 p-5 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">RecordLOGS could not open</h1>
          <p className="mt-2 text-sm text-slate-600">
            Refresh the page. If it stays blank, reset this browser session and sign in again.
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={resetSession}
              className="rounded-md border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700"
            >
              Reset session
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function App() {
  return (
    <Router>
      <AppErrorBoundary>
      <TutorialProvider>
      <TutorialOverlay />
      <AdminSessionGuard />
      <ScopedImpersonationGuard />
      <PresenceHeartbeat />
      <Routes>
        <Route path="/" element={<HomepageView />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/available-units" element={<AvailableUnitsView />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/user-guide" element={<UserGuidePage />} />

        <Route path="/dashboard" element={
          <PrivateRoute><Dashboard /></PrivateRoute>
        } />

        <Route path="/app/frp" element={
          <PrivateRoute><FRPModule /></PrivateRoute>
        } />

        <Route path="/app/groups" element={
          <PrivateRoute><GroupsModule /></PrivateRoute>
        } />

        <Route path="/app/ut-bills" element={
          <PrivateRoute><UTBillsModule /></PrivateRoute>
        } />

        <Route path="/admin" element={
          <PrivateRoute><AdminPage /></PrivateRoute>
        } />

        <Route path="/admin/sms" element={
          <PrivateRoute><AdminSMSPage /></PrivateRoute>
        } />

        <Route path="/admin/sms-history" element={
          <PrivateRoute><AdminSMSHistoryPage /></PrivateRoute>
        } />

        <Route path="/admin/email" element={
          <PrivateRoute><AdminEmailPage /></PrivateRoute>
        } />

        <Route path="/admin/email-history" element={
          <PrivateRoute><AdminEmailHistoryPage /></PrivateRoute>
        } />

        <Route path="/admin/add-deposit" element={
          <PrivateRoute><AdminAddDepositPage /></PrivateRoute>
        } />

        <Route path="/admin/manage-wallet" element={
          <PrivateRoute><AdminManageWalletPage /></PrivateRoute>
        } />

        <Route path="/admin/kra" element={
          <PrivateRoute><AdminKraPage /></PrivateRoute>
        } />

        <Route path="/admin/users" element={
          <PrivateRoute><AdminUsersPage /></PrivateRoute>
        } />

        <Route path="/support-tickets" element={
          <PrivateRoute><SupportTicketsPage /></PrivateRoute>
        } />

        <Route path="/admin/support-tickets" element={
          <PrivateRoute><AdminSupportTicketsPage /></PrivateRoute>
        } />

        <Route path="/admin/mpesa-reconciliation" element={
          <PrivateRoute><AdminMpesaReconciliationPage /></PrivateRoute>
        } />

        <Route path="/admin/mpesa-pull" element={
          <PrivateRoute><AdminMpesaPullPage /></PrivateRoute>
        } />

        <Route path="/admin/agency-trials" element={
          <PrivateRoute><AdminAgencyTrialsPage /></PrivateRoute>
        } />

        <Route path="/mpesa-reconciliation" element={
          <PrivateRoute><AdminMpesaReconciliationPage /></PrivateRoute>
        } />

        <Route path="/admin/send-bill" element={
          <PrivateRoute><AdminManageWalletPage /></PrivateRoute>
        } />

        <Route path="/locked" element={<LockedPage />} />

        <Route path="*" element={<HomepageView />} />
      </Routes>
      <AssistantChatWidget />
      <ImpersonationBanner />
      </TutorialProvider>
      </AppErrorBoundary>
    </Router>
  );
}

export default App;
